import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Shield, User, Briefcase, Users, CheckCircle, XCircle, Info } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import RbacCoverageAudit from '../../components/rbac/RbacCoverageAudit';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RoleManagement = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalClients: 0,
    adminCount: 0,
    operatorCount: 0,
    commercialCount: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const headers = { 'Authorization': 'Bearer cookie-auth' };

      // Fetch internal users
      const internalUsersResponse = await axios.get(`${API}/internal-users`, { headers });
      const internalUsers = internalUsersResponse.data || [];

      // Fetch clients count with pagination - use page=1 with small pageSize to get total
      const clientsResponse = await axios.get(`${API}/admin/users?pageSize=1&page=1`, { headers });
      // The response contains total count
      const totalUsers = clientsResponse.data?.total || 0;
      
      // Get sample to count roles
      const usersData = clientsResponse.data?.users || [];
      
      // For role counts, we only have internal users breakdown
      // Total clients = total users from API minus internal users
      const totalClients = totalUsers;

      setStats({
        totalUsers: internalUsers.length + totalClients,
        totalClients: totalClients,
        adminCount: internalUsers.filter(u => u.role === 'admin').length,
        operatorCount: internalUsers.filter(u => u.role === 'operateur').length,
        commercialCount: internalUsers.filter(u => u.role === 'commercial').length
      });

      setLoading(false);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les statistiques",
        variant: "destructive"
      });
      setLoading(false);
    }
  };

  const roles = [
    {
      id: 'admin',
      name: 'Administrateur',
      icon: Shield,
      color: 'purple',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      iconColor: 'text-purple-600',
      badgeColor: 'bg-purple-100 text-purple-800',
      count: stats.adminCount,
      description: 'Accès complet au système et gestion de tous les utilisateurs',
      permissions: [
        { name: 'Gestion des utilisateurs internes', allowed: true },
        { name: 'Gestion des clients', allowed: true },
        { name: 'Gestion des rôles et permissions', allowed: true },
        { name: 'Consultation des logs d\'audit', allowed: true },
        { name: 'Gestion complète des opérations', allowed: true },
        { name: 'Validation des devis et factures', allowed: true },
        { name: 'Assignation des opérateurs', allowed: true },
        { name: 'Configuration système', allowed: true },
        { name: 'Annulation d\'opérations', allowed: true }
      ]
    },
    {
      id: 'commercial',
      name: 'Commercial',
      icon: Briefcase,
      color: 'blue',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      iconColor: 'text-blue-600',
      badgeColor: 'bg-blue-100 text-blue-800',
      count: stats.commercialCount,
      description: 'Gestion des devis et relation client',
      permissions: [
        { name: 'Gestion des utilisateurs internes', allowed: false },
        { name: 'Gestion des clients', allowed: true },
        { name: 'Gestion des rôles et permissions', allowed: false },
        { name: 'Consultation des logs d\'audit', allowed: false },
        { name: 'Consultation des opérations', allowed: true },
        { name: 'Création et validation des devis', allowed: true },
        { name: 'Assignation des opérateurs', allowed: false },
        { name: 'Configuration système', allowed: false },
        { name: 'Annulation d\'opérations', allowed: false }
      ]
    },
    {
      id: 'operateur',
      name: 'Opérateur',
      icon: User,
      color: 'green',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      iconColor: 'text-green-600',
      badgeColor: 'bg-green-100 text-green-800',
      count: stats.operatorCount,
      description: 'Gestion opérationnelle des transports',
      permissions: [
        { name: 'Gestion des utilisateurs internes', allowed: false },
        { name: 'Gestion des clients', allowed: false },
        { name: 'Gestion des rôles et permissions', allowed: false },
        { name: 'Consultation des logs d\'audit', allowed: false },
        { name: 'Gestion des opérations assignées', allowed: true },
        { name: 'Mise à jour des statuts', allowed: true },
        { name: 'Upload de documents', allowed: true },
        { name: 'Configuration système', allowed: false },
        { name: 'Annulation d\'opérations', allowed: false }
      ]
    },
    {
      id: 'client',
      name: 'Client',
      icon: Users,
      color: 'gray',
      bgColor: 'bg-gray-50',
      borderColor: 'border-gray-200',
      iconColor: 'text-gray-600',
      badgeColor: 'bg-gray-100 text-gray-800',
      count: stats.totalClients,
      description: 'Utilisateurs externes de la plateforme',
      permissions: [
        { name: 'Gestion des utilisateurs internes', allowed: false },
        { name: 'Gestion des clients', allowed: false },
        { name: 'Gestion des rôles et permissions', allowed: false },
        { name: 'Consultation des logs d\'audit', allowed: false },
        { name: 'Création de demandes de transport', allowed: true },
        { name: 'Consultation de ses opérations', allowed: true },
        { name: 'Messagerie avec l\'équipe', allowed: true },
        { name: 'Téléchargement de documents', allowed: true },
        { name: 'Annulation de ses propres demandes', allowed: true }
      ]
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1db584]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-3xl font-bold text-gray-900">Gestion des Rôles</h1>
          <p className="text-gray-500 mt-1">Vue d'ensemble des rôles et permissions sur la plateforme</p>
        </div>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList data-testid="role-management-tabs">
          <TabsTrigger value="overview" data-testid="tab-roles-overview">Vue d'ensemble</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-rbac-audit">Audit de couverture</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6 mt-6">

      {/* Info Banner */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
            <div>
              <p className="text-sm text-gray-700">
                <strong>Système de rôles Baobab Connect</strong> : Chaque utilisateur possède un rôle qui 
                détermine ses permissions d'accès et ses capacités d'action sur la plateforme. Les rôles 
                sont hiérarchiques avec des niveaux de privilèges différents.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <Users className="w-8 h-8 mx-auto text-gray-500 mb-2" />
              <p className="text-sm text-gray-600">Utilisateurs Totaux</p>
              <p className="text-2xl font-bold">{stats.totalUsers}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-purple-200">
          <CardContent className="pt-6">
            <div className="text-center">
              <Shield className="w-8 h-8 mx-auto text-purple-600 mb-2" />
              <p className="text-sm text-gray-600">Administrateurs</p>
              <p className="text-2xl font-bold text-purple-600">{stats.adminCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-blue-200">
          <CardContent className="pt-6">
            <div className="text-center">
              <Briefcase className="w-8 h-8 mx-auto text-blue-600 mb-2" />
              <p className="text-sm text-gray-600">Commerciaux</p>
              <p className="text-2xl font-bold text-blue-600">{stats.commercialCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-green-200">
          <CardContent className="pt-6">
            <div className="text-center">
              <User className="w-8 h-8 mx-auto text-green-600 mb-2" />
              <p className="text-sm text-gray-600">Opérateurs</p>
              <p className="text-2xl font-bold text-green-600">{stats.operatorCount}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Roles Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {roles.map((role) => {
          const Icon = role.icon;
          return (
            <Card key={role.id} className={`${role.borderColor} border-2`}>
              <CardHeader className={role.bgColor}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-3 rounded-lg bg-white shadow-sm`}>
                      <Icon className={`w-6 h-6 ${role.iconColor}`} />
                    </div>
                    <div>
                      <CardTitle className="text-xl">{role.name}</CardTitle>
                      <Badge className={`${role.badgeColor} border-0 mt-1`}>
                        {role.count} {role.count === 1 ? 'utilisateur' : 'utilisateurs'}
                      </Badge>
                    </div>
                  </div>
                </div>
                <CardDescription className="mt-3 text-base">
                  {role.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <h4 className="font-semibold text-sm text-gray-700 mb-3 flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Permissions
                </h4>
                <div className="space-y-2">
                  {role.permissions.map((permission, index) => (
                    <div key={index} className="flex items-start gap-2">
                      {permission.allowed ? (
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      ) : (
                        <XCircle className="w-4 h-4 text-gray-300 mt-0.5 flex-shrink-0" />
                      )}
                      <span className={`text-sm ${permission.allowed ? 'text-gray-700' : 'text-gray-400'}`}>
                        {permission.name}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Future Feature Info */}
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-amber-600 mt-1 flex-shrink-0" />
            <div>
              <p className="text-sm text-gray-700">
                <strong>À venir</strong> : Système de permissions granulaires personnalisables, création de 
                rôles personnalisés, et gestion fine des accès par fonctionnalité.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="audit" className="mt-6">
          <RbacCoverageAudit />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default RoleManagement;
